import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../api/client';
import Modal from '../components/shared/Modal';
import EmptyState from '../components/shared/EmptyState';
import { Store, Plus, Pencil, Trash2 } from 'lucide-react';

export default function Shops() {
  const { t } = useTranslation();
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', domain: '', support_email: '', escalation_email: '', language: 'nl' });

  const fetchShops = useCallback(async () => {
    try {
      const response = await api.get('/shops');
      setShops(response.data);
    } catch (error) {
      console.error('Failed to fetch shops:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchShops(); }, [fetchShops]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', domain: '', support_email: '', escalation_email: '', language: 'nl' });
    setModalOpen(true);
  };

  const openEdit = (shop) => {
    setEditing(shop);
    setForm({
      name: shop.name,
      domain: shop.domain || '',
      support_email: shop.support_email || '',
      escalation_email: shop.escalation_email || '',
      language: shop.language || 'nl',
    });
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/shops/${editing.id}`, form);
      } else {
        await api.post('/shops', form);
      }
      setModalOpen(false);
      fetchShops();
    } catch (error) {
      console.error('Save failed:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('shops.delete_confirm'))) return;
    try {
      await api.delete(`/shops/${id}`);
      fetchShops();
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" /></div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('shops.title')}</h1>
        <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors">
          <Plus className="h-4 w-4" /> {t('shops.add')}
        </button>
      </div>

      {shops.length === 0 ? (
        <EmptyState
          icon={Store}
          title={t('shops.no_results')}
          description={t('shops.no_results_hint')}
          action={
            <button onClick={openCreate} className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors">
              {t('shops.add')}
            </button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {shops.map((shop) => (
            <div key={shop.id} className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-primary-100 flex items-center justify-center">
                    <Store className="h-5 w-5 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{shop.name}</h3>
                    {shop.domain && <p className="text-sm text-gray-500">{shop.domain}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => openEdit(shop)} className="p-1.5 text-gray-400 hover:text-primary-600 transition-colors">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button onClick={() => handleDelete(shop.id)} className="p-1.5 text-gray-400 hover:text-red-600 transition-colors">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="space-y-1 text-sm text-gray-500">
                {shop.support_email && <p>{t('shops.support')}: {shop.support_email}</p>}
                <p>{t('shops.language')}: {shop.language?.toUpperCase()}</p>
                <p>{t('shops.products')}: {shop.products?.length || 0}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? t('shops.edit_title') : t('shops.create_title')}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('shops.name')} *</label>
            <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('shops.domain')}</label>
            <input type="text" value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500" placeholder="example.com" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('shops.support_email')}</label>
            <input type="email" value={form.support_email} onChange={(e) => setForm({ ...form, support_email: e.target.value })} className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('shops.escalation_email')}</label>
            <input type="email" value={form.escalation_email} onChange={(e) => setForm({ ...form, escalation_email: e.target.value })} className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('shops.language')}</label>
            <select value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500">
              <option value="nl">Nederlands</option>
              <option value="en">English</option>
              <option value="fr">Français</option>
              <option value="de">Deutsch</option>
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setModalOpen(false)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">{t('common.cancel')}</button>
            <button type="submit" className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors">{editing ? t('common.update') : t('common.create')}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
